import React, { useMemo } from "react";
import { Autocomplete, TextField, useMediaQuery } from "@mui/material";

const CityAutocomplete = ({
  formData,
  setFormData,
  disabled,
  field,
  citiesData,
  statesData,
}: any) => {
  const isLargeScreen = useMediaQuery("(max-width: 600px)");

  const topCities = citiesData || [];

  const statesWithCities = useMemo(() => {
    const topCityIds = new Set(topCities.map((city: any) => city.id));
    return (
      statesData
        ?.map((state: any) => ({
          ...state,
          cities: state?.cities?.filter(
            (city: any) => !topCityIds.has(city.id)
          ),
        }))
        .filter((state: any) => state.cities.length > 0) || []
    );
  }, [statesData, topCities]);

  const cityOptions = useMemo(() => {
    const options = [];
    if (topCities.length > 0) {
      options.push({ label: "Top Cities", options: topCities });
    }

    statesWithCities.forEach((state: any) => {
      options.push({
        label: state.state_name,
        options: state.cities,
      });
    });

    return options;
  }, [topCities, statesWithCities]);

  return (
    <div className="font-sans">
      <div className="font-inter font-medium text-xs lg:text-sm text-[#404D61] px-1 flex items-center gap-x-2 mb-2">
        {field?.icon}
        <label className="text-sm lg:text-base">{field?.label}</label>
      </div>
      <Autocomplete
        disabled={disabled}
        value={
          formData?.city
            ? cityOptions
                .flatMap((group) => group.options)
                .find((city: any) => city.id === formData.city)
            : null
        }
        onChange={(event, newValue) => {
          const selectedState = statesData?.find((state: any) =>
            state.cities?.some((city: any) => city?.id === newValue?.id)
          );
          setFormData({
            ...formData,
            city: newValue ? newValue.id : null,
            state: selectedState ? selectedState?.id : null,
          });
        }}
        options={cityOptions.flatMap((group) => group.options)}
        groupBy={(option) => {
          const group = cityOptions.find((group) =>
            group.options.some((city: any) => city.id === option.id)
          );
          return group ? group.label : "";
        }}
        getOptionLabel={(city) => city.city_name || ""}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select city"
            required
            className="w-full text-xs lg:text-sm text-gray-600 "
            InputProps={{
              ...params.InputProps,
              sx: {
                padding: "2px 4px",
                fontSize: "0.875rem",
                height: "32px",
              },
            }}
          />
        )}
        renderGroup={(params) => (
          <div key={params?.key}>
            <div
              className="bg-gray-100 font-medium text-sm px-2 py-1"
              style={{
                borderBottom: "1px solid #E1E3E6",
              }}
            >
              {params?.group}
            </div>
            {params?.children}
          </div>
        )}
        ListboxProps={{
          style: {
            maxHeight: isLargeScreen ? "200px" : "300px",
            overflow: "auto",
            fontSize: "0.75rem",
            padding: "2px 4px",
          },
        }}
        sx={{
          width: "100%",
          "& .MuiAutocomplete-option": {
            py: 0.5,
            fontSize: "0.875rem",
          },
          "& .MuiAutocomplete-input": {
            px: 1,
            py: 0.5,
            fontSize: "0.875rem",
          },
          "& .MuiAutocomplete-option:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
        }}
      />
    </div>
  );
};

export default CityAutocomplete;
